import { createMastraBackend } from '@loadsmart/ai-chat'

import { statusFromChatError } from 'assistant/domain/chatErrors'

import { type ChatTokenStore } from './chatTokenStore'

// The library does not export this type by name.
export type ChatBackend = ReturnType<typeof createMastraBackend>

type AssistantBackendDependencies = {
  agentUrl: string
  tokenStore: Pick<ChatTokenStore, 'getToken' | 'invalidate'>
  /** Read for each send, so a new sign-in is picked up without a reload. */
  getOpendockToken: () => string | undefined
}

/**
 * The Mastra backend for the Assistant. Every request carries the chat token.
 * Only a send carries the Opendock token, because the agent needs it for the
 * data MCP and for nothing else.
 */
export function createAssistantBackend({
  agentUrl,
  tokenStore,
  getOpendockToken,
}: AssistantBackendDependencies): ChatBackend {
  const bearer = async () => ({ Authorization: `Bearer ${await tokenStore.getToken()}` })

  const base = createMastraBackend({
    prepareSendMessageRequest: async ({ agent, threadId, message, trigger }) => {
      const opendockToken = getOpendockToken()

      return {
        url: `${agentUrl}/chat/${encodeURIComponent(agent)}`,
        headers: {
          ...(await bearer()),
          ...(opendockToken ? { 'X-Opendock-Token': opendockToken } : {}),
        },
        body: {
          messages: message ? [message] : [],
          memory: { thread: threadId },
          ...(trigger === 'regenerate-message' ? { trigger } : {}),
        },
      }
    },
    prepareHydrateMessagesRequest: async ({ threadId }) => ({
      url: `${agentUrl}/chat/threads/${encodeURIComponent(threadId)}/messages`,
      headers: await bearer(),
    }),
    prepareResumeStreamRequest: async ({ threadId }) => ({
      url: `${agentUrl}/chat/threads/${encodeURIComponent(threadId)}/stream`,
      headers: await bearer(),
    }),
  })

  // A 401 means the agent ran nothing, so one more try with a new token is safe.
  async function retryOnceAfterUnauthorized<T>(call: () => Promise<T>): Promise<T> {
    try {
      return await call()
    } catch (error) {
      if (statusFromChatError(error) !== 401) throw error

      tokenStore.invalidate()

      return call()
    }
  }

  return Object.freeze({
    hydrateMessages: context => retryOnceAfterUnauthorized(() => base.hydrateMessages(context)),
    sendMessage: context => retryOnceAfterUnauthorized(() => base.sendMessage(context)),
    resumeStream: context => retryOnceAfterUnauthorized(() => base.resumeStream(context)),
  })
}
